import { type ApiClient, isApiError, listQueryParams } from "./client";
import type {
  ErrorFilters,
  ErrorGroup,
  ErrorStatus,
  HealthComponent,
  HealthStatus,
  ListQuery,
  Liveness,
  OverviewMetrics,
  Page,
  Readiness,
  SystemEvent,
  SystemHealth,
  SystemMetric,
} from "./types";

export interface SystemApi {
  health(): Promise<SystemHealth>;
  overview(): Promise<OverviewMetrics>;
  metrics(): Promise<SystemMetric[]>;
  errors(query: ListQuery<ErrorFilters>): Promise<Page<ErrorGroup>>;
  updateErrorStatus(id: string, status: ErrorStatus): Promise<ErrorGroup>;
  events(query: ListQuery): Promise<Page<SystemEvent>>;
}

const NO_SIGNAL = "No health signal (admin health endpoint unreachable)";

/** Worst status wins; UNKNOWN only when nothing is known. */
export function overallHealth(components: HealthComponent[]): HealthStatus {
  const known = components.filter((c) => c.status !== "UNKNOWN");
  if (!known.length) return "UNKNOWN";
  if (known.some((c) => c.status === "UNAVAILABLE")) return known.some((c) => c.status === "OPERATIONAL") ? "DEGRADED" : "UNAVAILABLE";
  if (known.some((c) => c.status === "DEGRADED")) return "DEGRADED";
  return "OPERATIONAL";
}

/**
 * Fallback platform health from the public probes (GET /health, GET /ready), used only when
 * the admin health endpoint itself fails. Twilio and queue state are unknown then.
 */
export function composeHealth(liveness: Liveness | Error, readiness: Readiness | Error, now = new Date()): SystemHealth {
  const at = now.toISOString();
  const apiUp = !(liveness instanceof Error);
  const ready = readiness instanceof Error ? null : readiness;
  const check = (ok: boolean | undefined): HealthStatus => (ok === undefined ? "UNKNOWN" : ok ? "OPERATIONAL" : "UNAVAILABLE");
  const readyDetail = readiness instanceof Error ? (isApiError(readiness) ? readiness.userMessage : readiness.message) : null;
  const components: HealthComponent[] = [
    {
      id: "api",
      name: "API",
      status: apiUp ? "OPERATIONAL" : "UNAVAILABLE",
      source: "GET /health",
      detail: apiUp ? null : (liveness as Error).message,
      checkedAt: at,
    },
    { id: "database", name: "PostgreSQL", status: check(ready?.checks.database), source: "GET /ready", detail: readyDetail, checkedAt: ready ? at : null },
    { id: "redis", name: "Redis", status: check(ready?.checks.redis), source: "GET /ready", detail: readyDetail, checkedAt: ready ? at : null },
    { id: "aiService", name: "AI Service", status: check(ready?.checks.aiService), source: "GET /ready", detail: readyDetail, checkedAt: ready ? at : null },
    { id: "twilio", name: "Twilio", status: "UNKNOWN", source: NO_SIGNAL, checkedAt: null },
    { id: "queue", name: "Queue", status: "UNKNOWN", source: NO_SIGNAL, checkedAt: null },
  ];
  return { overall: overallHealth(components), components, checkedAt: at };
}

export function createHttpSystemApi(client: ApiClient): SystemApi {
  const settle = <T>(p: Promise<T>) => p.catch((e: unknown) => (e instanceof Error ? e : new Error(String(e))));
  return {
    async health() {
      try {
        return await client.call<SystemHealth>("getSystemHealth");
      } catch (err) {
        if (isApiError(err) && (err.isUnauthenticated || err.isForbidden)) throw err;
        const [liveness, readiness] = await Promise.all([
          settle(client.call<Liveness>("getLiveness")),
          // /ready answers 503 with a valid body when a dependency is down.
          settle(client.call<Readiness>("getReadiness", { acceptStatuses: [503] })),
        ]);
        return composeHealth(liveness, readiness);
      }
    },
    overview: () => client.call("getOverviewMetrics"),
    metrics: async () => (await client.call<{ items: SystemMetric[] }>("listSystemMetrics")).items,
    errors: (q) => client.call("listSystemErrors", { query: listQueryParams(q) }),
    updateErrorStatus: (id, status) => client.call("updateSystemError", { params: { id }, body: { status } }),
    events: (q) => client.call("listSystemEvents", { query: listQueryParams(q) }),
  };
}
